import React, {useRef, useState, useEffect} from 'react';
import ReactPlayer from 'react-player';
import Entypo from 'react-native-vector-icons/Entypo';
import CCColors from '@/Utils/CCColors';
import Ionicons from 'react-native-vector-icons/Ionicons';
import Box from '../Box';
import {View, TouchableOpacity, Alert} from 'react-native';
import CCText from '../CCText';
import CCButton from '../CCButton';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {Picker} from '@react-native-picker/picker';
import ChessOverlayComponent from '@/Components/ChessOverlayComponent';
import Environment from '../../../environment';
import {Chess} from 'chess.js';
import TimelineWithPuzzles from './TimelineWithPuzzles';
import useCsocDemoVideoViewedApi from '@/Hooks/CommsApi/useCsocDemoVideoViewedApi';

// Fixed video_id for the CSOC Demo (a.k.a. zeroth-class video / induction).
// Marking this as viewed advances the comms_v2 'demo' track without requiring
// the Caissa demo session itself to be marked done by ops.
const CSOC_DEMO_VIDEO_ID = 14267;

const CustomVideoPlayer = ({selectedVideo, setCurrentActivePuzzle}) => {
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const mainRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPip, setIsPip] = useState(false);
  const [showChessOverlay, setShowChessOverlay] = useState(false);
  const [mode, setMode] = useState('analysis');
  const [puzzleActive, setPuzzleActive] = useState(false);

  const {mutate: markCsocDemoVideoViewed} = useCsocDemoVideoViewedApi();
  // Fire once per mount per video — onEnded can fire multiple times during seek.
  const csocDemoMarkedRef = useRef(false);

  // Handle volume changes
  const handleVolumeChange = e => {
    setVolume(parseFloat(e.target.value));
  };

  // Handle progress bar changes (scrubbing)
  const handleProgressChange = e => {
    const newProgress = parseFloat(e.target.value);
    setProgress(newProgress);
    playerRef.current?.seekTo(newProgress);
  };

  const maybeSetActivePuzzle = active_puzzle => {
    if (typeof setCurrentActivePuzzle === 'function' && active_puzzle) {
      setCurrentActivePuzzle({
        puzzleId: active_puzzle.id,
        puzzleName: active_puzzle.puzzle_name,
        totalMoveCount: active_puzzle.move_list.length,
        startingFEN: active_puzzle.starting_fen,
        orientation: active_puzzle.orientation,
        puzzleTime: active_puzzle.puzzle_time,
        createdAt: active_puzzle.created_at,
        remainingTime: active_puzzle?.remaining_time,
      });
    }
  };

  const captureVideoFrame = async () => {
    try {
      const player = playerRef.current;
      if (!player) {
        Alert.alert('Error', 'Video player not ready');
        return null;
      }

      const internalPlayer = player.getInternalPlayer();

      // Check if it's an HTML video element
      if (!(internalPlayer instanceof HTMLVideoElement)) {
        Alert.alert(
          'Screenshot Not Available',
          'Screenshots are only available for direct video files, not streaming services like YouTube or Vimeo.',
        );
        return null;
      }

      const video = internalPlayer;

      // Check if video is loaded
      if (video.readyState < 2) {
        Alert.alert('Error', 'Please wait for the video to load');
        return null;
      }

      // Create canvas with video's natural dimensions
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Check for valid dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        Alert.alert('Error', 'Invalid video dimensions');
        return null;
      }

      // Get 2D context and draw video frame
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        Alert.alert('Error', 'Could not create canvas context');
        return null;
      }

      // Draw the current video frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png');

      // Download the image
      // const link = document.createElement('a');
      // link.href = dataUrl;
      // link.download = `screenshot-${formatTime(playedSeconds).replace(':', '-')}.png`;

      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);

      return dataUrl;
    } catch (error) {
      console.error('Failed to capture video frame:', error);
      Alert.alert('Error', 'Failed to capture screenshot');
      return null;
    }
  };

  const CHESSVISION_API_URL = 'http://app.chessvision.ai/predict'; // replace with real endpoint
  const BACKEND_API_URL = Environment.CHESS_API_URL;
  // async function getFenFromFrame(dataUrl) {
  //   try {
  //     const payload = {
  //       image: dataUrl, // already has "data:image/png;base64,..."
  //       cropped: false,
  //       current_player: "white",
  //       board_orientation: "predict",
  //       predict_turn: true,
  //     };

  //     const res = await fetch(CHESSVISION_API_URL, {
  //       method: "POST",
  //       headers: {
  //         Accept: "application/json",
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify(payload),
  //     });

  //     if (!res.ok) {
  //       throw new Error(`ChessVision API failed: ${res.status}`);
  //     }

  //     const data = await res.json();
  //     const fen = data.result?.replace(/_/g, " ");
  //     return { fen, turn: data.turn };
  //   } catch (err) {
  //     console.error("FEN API error:", err);
  //     return null;
  //   }
  // }
  async function getFenFromFrame(dataUrl, videoId, timestamp) {
    try {
      const payload = {
        videoId,
        timestamp,
        frame: dataUrl, // already base64 with "data:image/png;base64,..."
      };

      const res = await fetch(`${BACKEND_API_URL}/video_learning/detect_fen`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Backend FEN API failed: ${res.status}`);
      }

      const data = await res.json();
      const fen = data.fen?.replace(/_/g, ' ');
      try {
        // This will throw an error if FEN is invalid
        const chess = new Chess(fen);
      } catch (error) {
        throw new Error(`Invalid FEN received: ${fen}`);
      }
      return {fen, turn: data.turn, cached: data.cached};
    } catch (err) {
      console.error('Backend FEN API error:', err);
      return null;
    }
  }

  async function getWebinarPuzzles(videoId) {
    try {
      const res = await fetch(
        `${BACKEND_API_URL}/video_learning/webinar-puzzles/${videoId}/`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        },
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch puzzles: ${res.status}`);
      }

      const data = await res.json();
      return data.puzzles || [];
    } catch (err) {
      console.error('Error fetching webinar puzzles:', err);
      return [];
    }
  }

  const [fen, SetFen] = useState('');

  /**
   * Handle screenshot button click
   */
  const handleScreenshot = async () => {
    // Pause the video first for better capture
    const wasPlaying = playing;
    if (wasPlaying) {
      setPlaying(false);
    }

    // Small delay to ensure video is paused
    setTimeout(async () => {
      const frameDataUrl = await captureVideoFrame();

      if (frameDataUrl) {
        const fenResult = await getFenFromFrame(
          frameDataUrl,
          selectedVideo.id,
          playedSeconds,
        );
        if (fenResult) {
          console.log('FEN detected:', fenResult.fen, 'turn:', fenResult.turn);
          SetFen(fenResult.fen);
          // setShowChessOverlay(true)
          if (isFullScreen) {
            handleFullScreen();
          }
        } else {
          Alert.alert('Error', 'Failed to extract FEN');
        }
      }

      // Resume playing if it was playing before
      if (wasPlaying) {
        setPlaying(true);
      }
    }, 100);
  };

  // Toggle play/pause state
  const handlePlayPause = () => {
    console.log('handleplay', playing);
    if (!playing) {
      // handleScreenshot();
      console.log('handleplay', playing);
      setTimeout(() => {
        setPuzzleActive(false);
        setMode('analysis');
      }, 1000);
    }
    setPlaying(!playing);
  };

  /**
   * Check if the current video URL supports screenshots
   */
  const isScreenshotSupported = () => {
    const url = selectedVideo?.s3_path;
    if (!url) return false;

    // List of domains that don't support screenshots
    const unsupportedDomains = [
      'youtube.com',
      'youtu.be',
      'vimeo.com',
      'soundcloud.com',
      'facebook.com',
      'twitch.tv',
      'dailymotion.com',
    ];

    return !unsupportedDomains.some(domain => url.includes(domain));
  };

  // Format time in mm:ss format
  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  //Handle rewind
  const handleSeekBackward = () => {
    playerRef.current?.seekTo(playerRef.current?.getCurrentTime() - 10);
  };
  //Handle seeking forward
  const handleSeekForward = () => {
    playerRef.current?.seekTo(playerRef.current?.getCurrentTime() + 10);
  };

  //Handle Mouse move for controls visibility
  let mouseTimeout;
  const handleMouseMove = () => {
    setShowControls(true);
    if (mouseTimeout) clearTimeout(mouseTimeout);
    mouseTimeout = setTimeout(() => setShowControls(false), 5000);
  };

  //Handle PictureInPicture
  const handlePictureInPicture = async () => {
    if (playerRef.current && !isPip) {
      try {
        await playerRef.current.getInternalPlayer().requestPictureInPicture();
        setIsPip(true);
      } catch (error) {
        console.error('Picture-in-Picture failed:', error);
      }
    } else if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
      setIsPip(false);
    }
  };

  // Handle Full Screen
  const handleFullScreen = () => {
    if (mainRef.current) {
      if (!document.fullscreenElement) {
        mainRef.current.requestFullscreen();
        setFullScreen(true);
      } else {
        document.exitFullscreen();
        setFullScreen(false);
      }
    } else {
      console.error('mainRef.current is undefined');
    }
  };

  // Add event listeners for fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        setFullScreen(true);
      } else {
        setFullScreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Add event listeners for pip change
  useEffect(() => {
    const handlePipChange = () => {
      if (!document.pictureInPictureElement) {
        setIsPip(false);
      }
    };

    document.addEventListener('leavepictureinpicture', handlePipChange);

    return () => {
      document.removeEventListener('leavepictureinpicture', handlePipChange);
    };
  }, []);

  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePuzzle, setActivePuzzle] = useState({});

  useEffect(() => {
    const fetchPuzzles = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getWebinarPuzzles(selectedVideo.id); // your standalone function
        setPuzzles(data);
      } catch (err) {
        console.error('Failed to fetch puzzles:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPuzzles();
  }, [selectedVideo.id]);

  useEffect(() => {
    if (!puzzles || !puzzles.length) return;
    if (playedSeconds == null) return;

    const puzzleToTrigger = puzzles.find(
      p => Math.floor(playedSeconds) === Math.floor(p.timestamp),
    );
    if (puzzleToTrigger) {
      if (isFullScreen) {
        handleFullScreen();
      }
      if (!puzzleActive && typeof setCurrentActivePuzzle !== 'function') {
        setPlaying(false); // pause video
        setActivePuzzle(puzzleToTrigger);
        setMode('puzzle');
        SetFen(puzzleToTrigger?.starting_fen);
        setPuzzleActive(true);
      } else {
        maybeSetActivePuzzle(puzzleToTrigger);
      }
    }
  }, [playedSeconds, puzzles]);

  useEffect(() => {
    const handleKeyDown = event => {
      switch (event.key) {
        case ' ':
          handlePlayPause();
          event.preventDefault();
          break;
        case 'ArrowRight':
          handleSeekForward();
          break;
        case 'ArrowLeft':
          handleSeekBackward();
          break;
        case 'f':
          handleFullScreen();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handlePlayPause,
    handleSeekForward,
    handleSeekBackward,
    handleFullScreen,
  ]);

  return (
    <View
      style={styles.container}
      ref={mainRef}
      //onMouseEnter={() => setShowControls(true)}
      //onMouseLeave={() => setShowControls(false)}
      onMouseMove={handleMouseMove}>
      {/* MiddleScreen Pause/Play button */}
      <View
        style={{
          ...styles.centerPlayPauseButton,
          opacity: showControls || !playing ? 1 : 0,
        }}>
        <TouchableOpacity onPress={handlePlayPause}>
          {playing ? (
            <Ionicons color="#fff" name={'pause'} size={40} />
          ) : (
            <Ionicons color="#fff" name={'play'} size={40} />
          )}
        </TouchableOpacity>
      </View>

      <View
        style={{
          ...styles.controls,
          opacity: showControls || !playing ? 1 : 0,
        }}>
        {/* Pause/Play button */}
        {/* <CCButton onPress={handlePlayPause}>
          {playing ? (
            <Ionicons color={CCColors.Black} name={'pause'} size={20} />
          ) : (
            <Ionicons color={CCColors.Black} name={'play'} size={20} />
          )}
        </CCButton> */}

        {/* Rewind button - button diabled but works with arrow key*/}
        {/* <TouchableOpacity onPress={handleSeekBackward}>
          <Icon name="replay-10" size={25} style={styles.fastBackwordButton} />
        </TouchableOpacity> */}

        {/* Fastforward button - button diabled but works with arrow key */}
        {/* <TouchableOpacity onPress={handleSeekForward}>
          <Icon name="forward-10" size={25} style={styles.fastForwardButton} />
        </TouchableOpacity> */}

        {/* Video Progress (timeline) */}
        <View style={styles.timelineContainer}>
          {playing && (
            <TimelineWithPuzzles
              duration={duration}
              progress={progress}
              puzzles={puzzles}
              onSeek={time => {
                playerRef.current.seekTo(time);
              }}
            />
          )}
        </View>

        {/* Top Row: Play, Volume, Timestamp, Speed, Fullscreen */}
        <View style={styles.topControlsRow}>
          {/* Play/Pause */}
          <View style={styles.gapInItems}>
            <TouchableOpacity onPress={handlePlayPause}>
              <Ionicons
                name={playing ? 'pause' : 'play'}
                color={CCColors.White}
                size={28}
              />
            </TouchableOpacity>

            {/* Timestamp */}
            {playing && (
              <View style={styles.timestamp}>
                <CCText color={CCColors?.White}>
                  {formatTime(playedSeconds)} / {formatTime(duration)}
                </CCText>
              </View>
            )}
          </View>

          <View style={styles.gapInItems}>
            {/* Volume Slider */}
            {playing && (
              <View style={styles.volumeRow}>
                <Ionicons name="volume-high" size={20} color={CCColors.White} />
                <input
                  type="range"
                  min={0}
                  max={1}
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  style={styles.volumeSlider}
                />
              </View>
            )}

            {/* PlaybackRate */}
            {playing && (
              <Picker
                selectedValue={playbackRate}
                style={styles.picker}
                onValueChange={itemValue => setPlaybackRate(itemValue)}>
                <Picker.Item label="0.25x" value={0.25} />
                <Picker.Item label="0.50x" value={0.5} />
                <Picker.Item label="0.75x" value={0.75} />
                <Picker.Item label="1.00x" value={1.0} />
                <Picker.Item label="1.25x" value={1.25} />
                <Picker.Item label="1.50x" value={1.5} />
                <Picker.Item label="1.75x" value={1.75} />
                <Picker.Item label="2.00x" value={2.0} />
              </Picker>
            )}

            {/* PictureInPicture - working but disabled for now*/}
            {/* <TouchableOpacity onPress={handlePictureInPicture}>
          <Icon
            name="picture-in-picture-alt"
            size={20}
            style={styles.pipButton}
          />
        </TouchableOpacity> */}

            {/* FullScreen */}
            {!playing && fen != '' && (
              <CCText fontSize={16} color={CCColors.White}>
                {' '}
                {mode == 'analysis'
                  ? 'would you like to analyse this position??'
                  : 'would you like to solve the class puzzle??'}
              </CCText>
            )}
            {!playing && fen != '' && (
              <CCButton
                onPress={() => {
                  setShowChessOverlay(true);
                }}>
                <CCText> Try Now </CCText>
              </CCButton>
            )}

            {playing && (
              <TouchableOpacity onPress={handleFullScreen}>
                {isFullScreen ? (
                  <Icon
                    name="fullscreen-exit"
                    size={20}
                    style={styles.fullScreenButton}
                  />
                ) : (
                  <Icon
                    name="fullscreen"
                    size={20}
                    style={styles.fullScreenButton}
                  />
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      <ReactPlayer
        ref={playerRef}
        url={selectedVideo.s3_path}
        playing={playing}
        volume={volume}
        width="100%"
        height="100%"
        controls={false}
        onProgress={state => {
          setProgress(state.played);
          setPlayedSeconds(state.playedSeconds);
        }}
        onDuration={duration => setDuration(duration)}
        onContextMenu={e => e.preventDefault()}
        playbackRate={playbackRate}
        onPause={() => {
          if (!puzzleActive) {
            handleScreenshot();
          }
        }}
        onPlaying={() => {
          SetFen('');
        }}
        onEnded={() => {
          // CSOC demo video reached the end — log to comms_v2 so the lifecycle
          // 'demo' track flips to done. Single-shot per mount; backend is also
          // idempotent (just stamps a timestamp).
          if (
            selectedVideo?.id === CSOC_DEMO_VIDEO_ID &&
            !csocDemoMarkedRef.current
          ) {
            csocDemoMarkedRef.current = true;
            try {
              markCsocDemoVideoViewed({viewedAt: new Date().toISOString()});
            } catch (e) {
              // Don't surface errors to the user — best-effort fire-and-forget.
              console.warn('csoc demo video viewed log failed', e);
            }
          }
        }}
        config={{
          file: {
            attributes: {
              crossOrigin: 'anonymous',
            },
          },
        }}
      />

      {showChessOverlay && (
        <ChessOverlayComponent
          fen={fen}
          onClose={() => {
            setShowChessOverlay(false);
            if (puzzleActive) {
              setMode('analysis');
              playerRef.current.seekTo(playerRef.current.getCurrentTime() + 1);
            }
          }}
          puzzle={activePuzzle}
          mode={mode}
        />
      )}

      {/* Custom controls */}
    </View>
  );
};

// Custom styles
const styles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
  },
  controls: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingVertical: 8,
    paddingHorizontal: 10,
    transition: 'opacity 0.3s ease-in-out',
    zIndex: 5,
  },
  topControlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  gapInItems: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  },
  volumeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  timelineContainer: {
    width: '100%',
  },
  playPauseButton: {
    backgroundColor: '#fff',
    border: 'none',
    padding: '10px',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  timeline: {
    width: '60%',
    margin: '0 10px',
    cursor: 'pointer',
  },
  volumeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: '10px',
  },
  volumeLabel: {
    color: '#fff',
    marginBottom: '5px',
    fontSize: '12px',
  },
  volumeSlider: {
    width: '100px',
    cursor: 'pointer',
  },
  timestamp: {
    color: '#fff',
    fontSize: '12px',
    flexDirection: 'row',
    padding: '5px',
  },
  fastBackwordButton: {
    color: '#fff',
    border: 'none',
    padding: '5px',
    paddingRight: '0px',
    borderRadius: '2px',
    cursor: 'pointer',
  },
  fastForwardButton: {
    color: '#fff',
    border: 'none',
    padding: '5px',
    paddingLeft: '0px',
    borderRadius: '2px',
    cursor: 'pointer',
  },
  picker: {
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    padding: '2px',
    borderRadius: '5px',
    cursor: 'pointer',
    textAlign: 'center',
    //alignItems: 'center',
    // justifyContent: 'center',
  },
  pipButton: {
    color: '#fff',
    border: 'none',
    padding: '5px',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  fullScreenButton: {
    //backgroundColor: '#fff', //change depending on button
    color: '#fff',
    border: 'none',
    padding: '5px',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  centerPlayPauseButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{translateX: -30}, {translateY: -30}], // Adjust for the button's size
    zIndex: 5,
  },
};

export default CustomVideoPlayer;
